// Backend response wrapper used by every endpoint. Mirrors
// betright-backend/src/utils/CreateAPIResponse.ts.
//
// A response with HTTP 200 but `success: false` is a logical failure
// (e.g. validation that the controller chose to surface as a structured
// payload rather than throwing). Callers must treat `success: false` the
// same as a thrown error — use `assertSuccess` to enforce this.

export interface ApiPagination {
  currentPage: number;
  totalPages: number;
  pageSize: number;
}

export interface ApiEnvelope<T = unknown> {
  success: boolean;
  statusCode: number;
  message: string;
  data?: T;
  pagination?: ApiPagination;
}

export interface ApiErrorPayload {
  code: string;
  // `field` is present for express-validator failures (one entry per
  // invalid field). For non-field errors (alternatives, generic
  // refinements) only `message` is set. Either may appear in the same
  // details array.
  details?: Array<{ field?: string; message: string }>;
}

export interface ApiErrorEnvelope {
  success: false;
  statusCode: number;
  message: string;
  error: ApiErrorPayload;
}

export function isApiEnvelope(value: unknown): value is ApiEnvelope {
  return (
    typeof value === "object" &&
    value !== null &&
    "success" in value &&
    "statusCode" in value &&
    "message" in value
  );
}

export function isApiErrorEnvelope(value: unknown): value is ApiErrorEnvelope {
  return (
    isApiEnvelope(value) &&
    value.success === false &&
    "error" in value &&
    typeof (value as ApiErrorEnvelope).error === "object"
  );
}
